import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ShoppingCart, Loader2, Printer, RefreshCw, AlertTriangle, PackageX } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtKg = (n) => (n == null ? '—' : `${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg`);

const runwayBadge = (months) => {
  if (months == null) return <span className="text-zinc-600">—</span>;
  const cls = months < 1 ? 'text-rose-400' : months < 2.5 ? 'text-amber-400' : 'text-emerald-400';
  return <span className={cls}>{months} mo</span>;
};

const PurchasePage = () => {
  const [targetKg, setTargetKg] = useState(24500);
  const [plan, setPlan] = useState(null);
  const [edited, setEdited] = useState({}); // product label -> kg override
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async (tkg) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/purchase/plan?target_kg=${tkg}`);
      if (r.ok) {
        setPlan(await r.json());
        setEdited({});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPlan(24500); }, [fetchPlan]);

  const qtyOf = useCallback((r) => {
    const o = edited[r.label];
    return o === undefined ? r.suggestedKg : (parseFloat(o) || 0);
  }, [edited]);

  const totalOrder = useMemo(
    () => (plan?.rows || []).reduce((s, r) => s + qtyOf(r), 0),
    [plan, qtyOf]
  );
  const delta = totalOrder - (parseFloat(targetKg) || 0);

  // Order lines for the supplier: products aggregated by their CURRENT coil spec
  const orderLines = useMemo(() => {
    const bySpec = {};
    for (const r of (plan?.rows || [])) {
      const qty = qtyOf(r);
      if (qty <= 0) continue;
      const key = `${r.sheetWidth}|${r.thickness}`;
      if (!bySpec[key]) bySpec[key] = { sheetWidth: r.sheetWidth, thickness: r.thickness, kg: 0, products: [] };
      bySpec[key].kg += qty;
      bySpec[key].products.push(r.label);
    }
    return Object.values(bySpec).sort((a, b) => b.kg - a.kg);
  }, [plan, qtyOf]);

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const handlePrint = () => {
    const rowsHtml = orderLines
      .map((l) => `<tr><td>${escapeHtml(l.sheetWidth)} mm</td><td>${escapeHtml(l.thickness)} mm</td><td style="text-align:right">${l.kg.toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg</td><td style="color:#666;font-size:11px">${escapeHtml(l.products.join(', '))}</td></tr>`)
      .join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Coil Order — Suteko Steel</title>
      <style>
        body{font-family:Arial,sans-serif;color:#111;padding:32px}
        h1{font-size:20px;margin:0 0 4px}
        .sub{color:#555;font-size:12px;margin-bottom:20px}
        table{border-collapse:collapse;width:100%;max-width:720px}
        th,td{border:1px solid #bbb;padding:8px 12px;font-size:13px;text-align:left}
        th{background:#f0f0f0;text-transform:uppercase;font-size:11px}
        tfoot td{font-weight:bold;background:#fafafa}
      </style></head><body onload="window.print()">
      <h1>Coil Order — Suteko Steel</h1>
      <div class="sub">Generated ${new Date().toLocaleDateString('en-GB')} · target ${Number(targetKg).toLocaleString('en-GB')} kg</div>
      <table><thead><tr><th>Sheet width</th><th>Thickness</th><th style="text-align:right">Quantity</th><th>For products</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr><td colspan="2">Total</td><td style="text-align:right">${totalOrder.toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg</td><td></td></tr></tfoot>
      </table></body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) { window.alert('Please allow popups to print the order.'); return; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="purchase-page">
      <div className="flex items-center gap-3 mb-1">
        <ShoppingCart className="w-6 h-6 text-amber-500" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Purchase Planner</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Suggested coil order per product, based on historical consumption and current usable stock.
        Quantities are editable before printing.
      </p>

      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Truck target (kg)</label>
          <Input
            type="number"
            step="100"
            value={targetKg}
            onChange={(e) => setTargetKg(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-zinc-200 w-36"
            data-testid="purchase-target-input"
          />
        </div>
        <Button
          onClick={() => fetchPlan(parseFloat(targetKg) || 0)}
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2 h-9"
          data-testid="purchase-recalc-btn"
        >
          <RefreshCw className="w-4 h-4" /> Recalculate
        </Button>
        <Button
          onClick={handlePrint}
          variant="outline"
          disabled={orderLines.length === 0}
          className="border-zinc-700 text-zinc-300 gap-2 h-9"
          data-testid="purchase-print-btn"
        >
          <Printer className="w-4 h-4" /> Print order
        </Button>
        {plan && (
          <div className="text-xs text-zinc-500 ml-auto self-center" data-testid="purchase-meta">
            History: {plan.historyMonths} months · projected coverage ≈ <span className="text-zinc-300">{plan.horizonMonths} months</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : !plan ? (
        <div className="text-zinc-500 text-sm">Could not load plan.</div>
      ) : (
        <>
          {/* Per-product plan */}
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase">
                  <th className="text-left py-3 px-4 font-medium">Product</th>
                  <th className="text-right py-3 px-3 font-medium hidden sm:table-cell">Coil spec</th>
                  <th className="text-right py-3 px-3 font-medium hidden sm:table-cell">Used /mo</th>
                  <th className="text-right py-3 px-3 font-medium">Usable stock</th>
                  <th className="text-right py-3 px-3 font-medium hidden sm:table-cell">Runway</th>
                  <th className="text-right py-3 px-4 font-medium w-32">Order (kg)</th>
                </tr>
              </thead>
              <tbody>
                {plan.rows.map((r) => {
                  const qty = qtyOf(r);
                  return (
                    <tr key={r.label} className={`border-b border-zinc-800/50 last:border-0 ${qty > 0 ? '' : 'opacity-50'}`} data-testid={`purchase-row-${r.label.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                      <td className="py-2.5 px-4">
                        <span className="text-zinc-200 font-medium">{r.label}</span>
                        {r.historicalSpecs.length > 1 && (
                          <span className="block text-[10px] text-zinc-600">previous widths: {r.historicalSpecs.join(', ')} mm</span>
                        )}
                      </td>
                      <td className="text-right py-2.5 px-3 text-zinc-400 text-sm hidden sm:table-cell whitespace-nowrap">{r.sheetWidth} × {r.thickness}</td>
                      <td className="text-right py-2.5 px-3 text-zinc-400 text-sm hidden sm:table-cell">{fmtKg(r.ratePerMonth)}</td>
                      <td className="text-right py-2.5 px-3 text-zinc-300 text-sm">{fmtKg(r.stockKg)}</td>
                      <td className="text-right py-2.5 px-3 text-sm hidden sm:table-cell">{runwayBadge(r.runwayMonths)}</td>
                      <td className="py-2 px-4">
                        <Input
                          type="number"
                          step="25"
                          min="0"
                          value={edited[r.label] !== undefined ? edited[r.label] : r.suggestedKg}
                          onChange={(e) => setEdited({ ...edited, [r.label]: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-zinc-200 text-right h-8"
                          data-testid={`purchase-qty-${r.label.replace(/[^a-zA-Z0-9]/g, '-')}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-end gap-4 text-sm" data-testid="purchase-totals">
            <span className="text-zinc-500">Order total:</span>
            <span className="text-lg font-bold text-white" data-testid="purchase-total-kg">{fmtKg(totalOrder)}</span>
            <span className={`flex items-center gap-1 text-xs ${Math.abs(delta) <= 500 ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="purchase-delta">
              {Math.abs(delta) > 500 && <AlertTriangle className="w-3.5 h-3.5" />}
              {delta >= 0 ? '+' : ''}{Math.round(delta).toLocaleString('en-GB')} kg vs target
            </span>
          </div>

          {/* Order summary by coil spec — what actually goes to the supplier */}
          {orderLines.length > 0 && (
            <div className="mt-8" data-testid="order-summary-section">
              <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide mb-2">Order summary — by coil spec</h2>
              <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase">
                      <th className="text-left py-2.5 px-4 font-medium">Sheet width</th>
                      <th className="text-left py-2.5 px-3 font-medium">Thickness</th>
                      <th className="text-right py-2.5 px-3 font-medium">Quantity</th>
                      <th className="text-left py-2.5 px-4 font-medium hidden sm:table-cell">For products</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderLines.map((l) => (
                      <tr key={`${l.sheetWidth}|${l.thickness}`} className="border-b border-zinc-800/50 last:border-0" data-testid={`order-line-${l.sheetWidth}-${l.thickness}`}>
                        <td className="py-2.5 px-4 text-zinc-200 font-medium">{l.sheetWidth} mm</td>
                        <td className="py-2.5 px-3 text-zinc-300">{l.thickness} mm</td>
                        <td className="py-2.5 px-3 text-right text-amber-400 font-semibold">{fmtKg(l.kg)}</td>
                        <td className="py-2.5 px-4 text-xs text-zinc-500 hidden sm:table-cell">{l.products.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stock nobody claims */}
          {plan.unmatchedStock?.length > 0 && (
            <div className="mt-8" data-testid="unmatched-stock-section">
              <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                <PackageX className="w-4 h-4 text-zinc-500" />
                Unassigned stock ({fmtKg(plan.unmatchedStockKg)})
              </h2>
              <p className="text-xs text-zinc-600 mb-2">Coils in stock that are not linked to any active product.</p>
              <div className="flex flex-wrap gap-2">
                {plan.unmatchedStock.map((u) => (
                  <span key={`${u.sheetWidth}|${u.thickness}`} className="text-xs bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-zinc-400">
                    {u.sheetWidth} × {u.thickness} mm — <span className="text-zinc-300">{fmtKg(u.stockKg)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PurchasePage;
